import { useEffect, useRef } from 'react';

const partners = [
  { name: 'Centre 105', category: 'Charity' },
  { name: 'Chesterville Fair', category: 'Festival' },
  { name: 'Trainfest', category: 'Festival' },
  { name: 'Upper Canada Village', category: 'Business' },
  { name: 'Optimist Club of Cornwall', category: 'Business' },
  { name: 'Children\'s Treatment Centre', category: 'Charity' },
  { name: 'Cornwall Rotary Club', category: 'Business' },
  { name: 'Lights of the St. Lawrence', category: 'Festival' },
  { name: 'Stormont County Fair', category: 'Festival' },
  { name: 'Lions Club of Ingleside', category: 'Charity' },
  { name: 'Kinsmen Club of Cornwall', category: 'Charity' },
  { name: 'Food Basics', category: 'Business' },
  { name: 'Supply Chain Management', category: 'Business' },
  { name: 'Super 8', category: 'Business' },
  { name: 'Children\'s Hospital of Eastern Ontario', category: 'Business' },
  { name: 'Clifford Bowey Public School', category: 'Schools' },
  { name: 'Crystal Bay Centre for Special Education', category: 'Schools' },
];

const categoryIcon: Record<string, string> = {
  Festival: 'ri-star-smile-line',
  Business: 'ri-building-2-line',
  Charity: 'ri-heart-3-line',
  Schools: 'ri-graduation-cap-line',
};

const categoryColor: Record<string, string> = {
  Festival: 'text-yellow-400',
  Business: 'text-white/70',
  Charity: 'text-red-400',
  Schools: 'text-green-400',
};

export default function CharityEvents() {
  const track1 = useRef<HTMLDivElement>(null);
  const track2 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tracks = [track1.current, track2.current];
    let animFrames: number[] = [];
    let positions = [0, 0];

    tracks.forEach((track, i) => {
      if (!track) return;
      const speed = i === 0 ? 0.5 : 0.4;
      const direction = i === 0 ? -1 : 1;

      const animate = () => {
        positions[i] += speed * direction;
        const half = track.scrollWidth / 2;
        if (direction === -1 && Math.abs(positions[i]) >= half) positions[i] = 0;
        if (direction === 1 && positions[i] >= 0) positions[i] = -half;
        track.style.transform = `translateX(${positions[i]}px)`;
        animFrames[i] = requestAnimationFrame(animate);
      };

      animFrames[i] = requestAnimationFrame(animate);
    });

    return () => animFrames.forEach(cancelAnimationFrame);
  }, []);

  const half = partners.slice(0, Math.ceil(partners.length / 2));
  const secondHalf = partners.slice(Math.ceil(partners.length / 2));

  const renderCard = (p: typeof partners[0], idx: number) => (
    <div
      key={idx}
      className="flex-shrink-0 flex items-center gap-3 px-6 py-4 bg-zinc-900 border border-white/10 rounded-xl mx-3 hover:border-red-600 transition-colors duration-300"
    >
      <div className={`w-8 h-8 flex items-center justify-center ${categoryColor[p.category]}`}>
        <i className={`${categoryIcon[p.category]} text-xl`}></i>
      </div>
      <div>
        <p className="text-white font-semibold text-sm whitespace-nowrap">{p.name}</p>
        <p className={`text-xs font-medium ${categoryColor[p.category]}`}>{p.category}</p>
      </div>
    </div>
  );

  return (
    <section id="partners" className="py-24 bg-black overflow-hidden">
      <div className="container mx-auto px-6 mb-14">
        <div className="text-center">
          <div className="inline-block mb-6 px-6 py-2 bg-red-600 rounded-full">
            <span className="text-white text-sm font-semibold uppercase tracking-wide">Our Community</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Festivals, Businesses &amp; Charities
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Proud to have worked alongside these amazing organizations across Eastern Ontario
          </p>
        </div>
      </div>

      {/* Row 1 — scrolls left */}
      <div className="relative mb-5 overflow-hidden">
        <div className="flex" ref={track1}>
          {[...half, ...half].map((p, i) => renderCard(p, i))}
        </div>
      </div>

      {/* Row 2 — scrolls right */}
      <div className="relative overflow-hidden">
        <div className="flex" ref={track2} style={{ transform: `translateX(-${secondHalf.length * 220}px)` }}>
          {[...secondHalf, ...secondHalf].map((p, i) => renderCard(p, i))}
        </div>
      </div>

      {/* Legend */}
      <div className="container mx-auto px-6 mt-12">
        <div className="flex items-center justify-center gap-8 flex-wrap">
          {Object.entries(categoryIcon).map(([cat, icon]) => (
            <div key={cat} className="flex items-center gap-2">
              <div className={`w-5 h-5 flex items-center justify-center ${categoryColor[cat]}`}>
                <i className={`${icon} text-base`}></i>
              </div>
              <span className="text-white/50 text-sm">{cat}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
